import type { Money } from '@dastarhan/contracts';
import { lineSum, type OrderItem } from './order';

/**
 * Движок акций — чистая функция. Правила приезжают с каталогом,
 * поэтому офлайн-касса считает акции ровно так же, как посчитал бы
 * сервер. Каждая акция считается от «сырого» подытога независимо;
 * суммарная скидка ограничена подытогом.
 */

export type PromoRule =
  | { type: 'HAPPY_HOURS'; percent: number; days: number[]; fromMin: number; toMin: number }
  | { type: 'N_PLUS_ONE'; productId: string; n: number };

export interface Promo { id: string; name: string; rule: PromoRule }

export interface PromoResult {
  discount: Money;
  applied: { name: string; amount: Money }[];
}

export function applyPromos(
  items: Pick<OrderItem, 'productId' | 'qty' | 'unitPrice' | 'modifiers' | 'removed'>[],
  promos: Promo[],
  now: Date,
): PromoResult {
  const live = items.filter((i) => !i.removed);
  const subtotal = live.reduce((s, i) => s + lineSum(i), 0);
  if (subtotal === 0) return { discount: 0, applied: [] };

  const applied: { name: string; amount: Money }[] = [];
  const minutes = now.getHours() * 60 + now.getMinutes();
  const day = now.getDay();

  for (const p of promos) {
    const r = p.rule;
    if (r.type === 'HAPPY_HOURS') {
      if (!r.days.includes(day)) continue;
      if (minutes < r.fromMin || minutes >= r.toMin) continue;
      const amount = Math.round((subtotal * r.percent) / 100);
      if (amount > 0) applied.push({ name: p.name, amount });
    } else {
      // N+1: за каждые (n+1) единиц товара — одна бесплатно.
      // Весовые доли не участвуют: бесплатной бывает целая единица.
      const lines = live.filter((i) => i.productId === r.productId);
      const totalQty = Math.floor(lines.reduce((s, i) => s + i.qty, 0));
      const freeUnits = Math.floor(totalQty / (r.n + 1));
      if (freeUnits === 0) continue;
      const unitPrice = Math.min(...lines.map((i) => i.unitPrice));
      applied.push({ name: p.name, amount: freeUnits * unitPrice });
    }
  }

  const discount = Math.min(subtotal, applied.reduce((s, a) => s + a.amount, 0));
  return { discount, applied };
}
